import { Config } from 'config/envConfig';
import React, { useEffect, useState } from 'react';
import { Card, Badge, message, Modal } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { loadPaymentScript } from '../utils/loadPaymentScript';
import { getUrl } from './pdfDisplay/utils';
import { usePlanData } from '../hooks/usePlanData';
import { SUBSCRIPTION_PLANS, CREDIT_TOOLTIP, PLAN_DISPLAY, POSTPAID_FALLBACK_FEATURES } from '../utils/planUtils';
import { LoadingView, Button } from '@quyva/shared-ui';

// ---------- helpers -------------------------------------------------------

/** Format a price: symbol + whole number, e.g. $20, ₹1,670, €18 */
const fmtLocalPrice = (amount, currencyCode) => {
    try {
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: currencyCode,
            currencyDisplay: 'narrowSymbol',
            maximumFractionDigits: 0,
        }).format(amount);
    } catch { return `${amount} ${currencyCode}`; }
};

// ---------- component -------------------------------------------------------

const PricingPageOnLogin = () => {
    const { user } = useAuth();
    const { error: plansError, currencyInfo, plans } = usePlanData(user?.email);

    const [currentPlanName, setCurrentPlanName] = useState(SUBSCRIPTION_PLANS.FREE);
    const [isLoading, setIsLoading] = useState(false);
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancellingSubscription, setCancellingSubscription] = useState(false);

    useEffect(() => { if (plansError) message.error(plansError); }, [plansError]);

    useEffect(() => {
        const fetchCurrentPlan = async () => {
            setIsLoading(true);
            try {
                const res = await axios.get(
                    getUrl(`http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/subscription_routes/current-plan/${encodeURIComponent(user.email)}`)
                );
                // Prefer plan_type (logical key) over name (DB label like 'PRO', 'ENTERPRISE')
                // so card "Current" badge matches correctly regardless of the DB name value.
                const planType = res.data.plan_type ?? res.data.name ?? SUBSCRIPTION_PLANS.FREE;
                setCurrentPlanName(planType);
            } catch {
                setCurrentPlanName(SUBSCRIPTION_PLANS.FREE);
            } finally {
                setIsLoading(false);
            }
        };
        if (user?.email) fetchCurrentPlan();
    }, [user]);

    // ---- payment ------------------------------------------------------------

    const handleCheckout = async () => {
        const currency_code = currencyInfo.currency_code;
        const exchangeRate = currencyInfo.rate || 1;
        const local_price = currencyInfo.local_price || Math.round(currencyInfo.usd_price * exchangeRate);
        try {
            setCheckoutLoading(true);
            const res = await axios.post(
                getUrl(`http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/create-checkout-session`),
                { amount: local_price, currency: currency_code, isYearly: false, exchangeRate },
                { headers: { 'Content-Type': 'application/json', Authorization: encodeURIComponent(user.token) } }
            );
            if (res?.data) {
                loadPaymentScript(user, {
                    amount:   res.data.amount,
                    order_id: res.data.order_id,
                    currency: res.data.currency,
                });
            }
        } catch (err) {
            message.error(err.message || 'Payment failed. Please try again.');
        } finally {
            setCheckoutLoading(false);
        }
    };

    const handleCancelSubscription = async () => {
        setCancellingSubscription(true);
        try {
            const res = await axios.post(
                getUrl(`http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/cancel-subscription/current`),
                { email: user.email },
                {
                    headers:         { 'Content-Type': 'application/json', Authorization: encodeURIComponent(user.token) },
                    withCredentials: true,
                }
            );
            message.success('Subscription cancelled successfully');
            setShowCancelModal(false);
            window.location.reload();
        } catch {
            message.error('Failed to cancel subscription');
        } finally {
            setCancellingSubscription(false);
        }
    };

    // ---- helpers ------------------------------------------------------------

    const isPlanCurrent = (key) => key === currentPlanName;

    const canUpgrade = (key) =>
        key !== SUBSCRIPTION_PLANS.FREE &&
        !isPlanCurrent(key);

    // Derived values — currency comes from user/org profile (never from a dropdown)
    const activeCurrency  = currencyInfo.currency_code || 'USD';
    const activeRate      = currencyInfo.rate || 1;
    const postpaidPlan    = plans.find(p => p.plan_type === SUBSCRIPTION_PLANS.PAY_AS_YOU_GO);

    // ── render ────────────────────────────────────────────────────────────────
    return (
        <div className='flex flex-col items-center max-w-[1440px] mx-auto px-2'>
            {isLoading ? (
                <div className='flex justify-center items-center w-full py-8'>
                    <LoadingView loadingText='Loading subscription plans…' />
                </div>
            ) : (
                <>
                    {/* INR exchange rate hint */}
                    {!isPlanCurrent(SUBSCRIPTION_PLANS.PAY_AS_YOU_GO) && activeCurrency === 'INR' && activeRate > 1 && (
                        <div className='mt-8 pt-4 flex flex-col items-center justify-center gap-1'>
                            <span className='text-xs text-gray-400'>
                                1 USD = ₹{activeRate.toFixed(2)}
                                {currencyInfo.rate_locked ? ' · locked at purchase' : ' · live rate'}
                            </span>
                        </div>
                    )}

                    {/* ── Plan cards: all three shown together, unless postpaid is the active plan ── */}
                    <div className='flex flex-wrap justify-center items-stretch gap-4 p-4 w-full mt-4'>

                        {/* ── PREPAID plans (sourced from DB via usePlanData) — hidden once postpaid is active ── */}
                        {!isPlanCurrent(SUBSCRIPTION_PLANS.PAY_AS_YOU_GO) && plans
                            .filter(p => p.plan_type === SUBSCRIPTION_PLANS.FREE || p.plan_type === SUBSCRIPTION_PLANS.TOP_PLAN)
                            .map((plan) => {
                            const planType     = plan.plan_type;
                            const display      = PLAN_DISPLAY[planType] || { label: plan.name, period: '' };
                            const isCurrent    = isPlanCurrent(planType);
                            const isPopular    = !!plan.is_most_popular;
                            const isFree       = planType === SUBSCRIPTION_PLANS.FREE;
                            const isTopCurrent = isCurrent && planType === SUBSCRIPTION_PLANS.TOP_PLAN;
                            const planFeatures = Array.isArray(plan.features) ? plan.features : [];

                            return (
                                <Badge.Ribbon
                                    key={planType}
                                    text={isCurrent ? 'Current' : isPopular ? 'Most Popular' : ''}
                                    color={isCurrent ? 'yellow' : isPopular ? 'blue' : 'white'}
                                >
                                    <Card
                                        className='w-64 h-[420px] rounded-xl shadow-md hover:shadow-lg transition duration-200 flex flex-col flex-shrink-0'
                                        title={display.label}
                                        headStyle={{
                                            backgroundColor: isPopular ? '#e0f7ff' : 'white',
                                            textAlign: 'center',
                                            fontWeight: 'bold',
                                            padding: '8px',
                                        }}
                                        bodyStyle={{
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            padding: '12px',
                                        }}
                                    >
                                        {/* Price */}
                                        <div className='text-center mb-1'>
                                            <div className='text-2xl font-semibold'>
                                                {fmtLocalPrice(
                                                    isFree ? 0 : Math.round(plan.price * activeRate),
                                                    activeCurrency
                                                )}
                                            </div>
                                            {/* USD reference for non-USD currencies */}
                                            {!isFree && activeCurrency !== 'USD' && (
                                                <div className='text-xs text-gray-400 mt-0.5'>
                                                    ≈ ${plan.price} USD
                                                </div>
                                            )}
                                            <div className='text-xs mt-1 text-gray-500'>
                                                {display.period}
                                            </div>
                                        </div>

                                        {/* Credits */}
                                        <div
                                            className='text-center text-xs text-blue-600 font-medium mt-1 mb-3'
                                            title={CREDIT_TOOLTIP}
                                            style={{ cursor: 'help' }}
                                        >
                                            {(plan.total_queries || 0).toLocaleString()} credits
                                        </div>

                                        {/* Features */}
                                        <ul className='flex-grow mt-1 mb-4 text-sm space-y-2'>
                                            {planFeatures.map((f, i) => (
                                                <li
                                                    key={i}
                                                    className={`flex items-start ${f === '' ? 'invisible' : 'gap-2'}`}
                                                >
                                                    <span className='text-green-500 mt-0.5'>
                                                        <CheckOutlined style={{ fontSize: '12px' }} />
                                                    </span>
                                                    <span className='text-gray-800 text-sm'>{f}</span>
                                                </li>
                                            ))}
                                        </ul>

                                        {/* CTA */}
                                        {!isFree && (
                                            <Button
                                                text={
                                                    isTopCurrent
                                                        ? 'Cancel Subscription'
                                                        : `Upgrade to ${display.label}`
                                                }
                                                type={isTopCurrent ? 'danger' : 'success'}
                                                onClick={() =>
                                                    isTopCurrent
                                                        ? setShowCancelModal(true)
                                                        : handleCheckout()
                                                }
                                                disabled={checkoutLoading || (!isTopCurrent && !canUpgrade(planType))}
                                                className='w-full'
                                            />
                                        )}
                                    </Card>
                                </Badge.Ribbon>
                            );
                        })}

                        {/* ── POSTPAID plan (sourced from DB via usePlanData) — always shown ── */}
                        <Badge.Ribbon
                            text={isPlanCurrent(SUBSCRIPTION_PLANS.PAY_AS_YOU_GO) ? 'Current' : ''}
                            color='yellow'
                            style={{ display: isPlanCurrent(SUBSCRIPTION_PLANS.PAY_AS_YOU_GO) ? undefined : 'none' }}
                        >
                            <Card
                                className='w-64 h-[420px] rounded-xl shadow-md hover:shadow-lg transition duration-200 flex flex-col flex-shrink-0'
                                title={PLAN_DISPLAY[SUBSCRIPTION_PLANS.PAY_AS_YOU_GO].label}
                                headStyle={{
                                    backgroundColor: 'white',
                                    textAlign: 'center',
                                    fontWeight: 'bold',
                                    padding: '8px',
                                }}
                                bodyStyle={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    padding: '12px',
                                }}
                            >
                                {/* Description */}
                                <div className='text-center text-xs text-gray-500 mb-4 mt-1'>
                                    {postpaidPlan?.description || 'No upfront charge. Pay based on actual usage at end of each month.'}
                                </div>

                                {/* Features */}
                                <ul className='flex-grow mt-1 mb-4 text-sm space-y-2'>
                                    {(Array.isArray(postpaidPlan?.features) && postpaidPlan.features.length > 0
                                    ? postpaidPlan.features
                                    : POSTPAID_FALLBACK_FEATURES
                                ).map((f, i) => (
                                        <li key={i} className='flex items-start gap-2'>
                                            <span className='text-green-500 mt-0.5'>
                                                <CheckOutlined style={{ fontSize: '12px' }} />
                                            </span>
                                            <span className='text-gray-800 text-sm'>{f}</span>
                                        </li>
                                    ))}
                                </ul>

                                {/* Contact Sales */}
                                <Button
                                    text={postpaidPlan?.button_text || 'Contact Sales'}
                                    type='success'
                                    onClick={() => { window.location.href = '/contactUs'; }}
                                    className='w-full'
                                />
                            </Card>
                        </Badge.Ribbon>
                    </div>
                </>
            )}

            {/* ── Cancel confirmation modal ── */}
            <Modal
                title='Cancel Subscription'
                open={showCancelModal}
                onCancel={() => setShowCancelModal(false)}
                footer={null}
            >
                {cancellingSubscription ? (
                    <LoadingView loadingText='Cancelling subscription…' />
                ) : (
                    <div className='space-y-4'>
                        <h3 className='text-lg font-medium'>
                            Are you sure you want to cancel your subscription?
                        </h3>
                        <div className='text-sm'>
                            <p>This action will:</p>
                            <ul className='list-disc pl-5 mt-2 space-y-2'>
                                <li>Cancel your current subscription immediately</li>
                                <li>Remove access to Pro plan features</li>
                                <li>Downgrade your account to the Free plan ({(plans.find(p => p.plan_type === SUBSCRIPTION_PLANS.FREE)?.total_queries ?? 500).toLocaleString()} credits)</li>
                            </ul>
                        </div>
                        <div className='flex gap-3 justify-end mt-4'>
                            <Button
                                text='Keep Subscription'
                                type='default'
                                onClick={() => setShowCancelModal(false)}
                            />
                            <Button
                                text='Cancel Subscription'
                                type='danger'
                                onClick={handleCancelSubscription}
                                disabled={cancellingSubscription}
                            />
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default PricingPageOnLogin;
